// Canonical reaction set + toggle decision shared across surfaces that
// support post-style reactions (feed posts, resources, …). Server schemas
// reference REACTION_KINDS for column enums and Zod validators; the
// ReactionPicker UI reads the same list so symbols stay aligned across the
// network boundary.
import { z } from 'zod';
// Stored kind → the label members see. The stored names predate the current
// labels and are kept (renaming a stored value would need a multi-deploy
// migration), so never wire a kind by reading its name as its label:
//   support    → Love
//   helpful    → Celebrate
//   insightful → Helpful
//   same       → Support (labelled Care until 2026-10)
//   relate     → Relate ("I've been there too")
export const REACTION_KINDS = [
    'support',
    'insightful',
    'same',
    'helpful',
    'relate',
];
/** The all-zero tally every reaction surface starts from. Callers that need a
 *  mutable copy spread it (`{ ...ZERO_REACTION_COUNTS }`). */
export const ZERO_REACTION_COUNTS = {
    support: 0,
    insightful: 0,
    same: 0,
    helpful: 0,
    relate: 0,
};
/** The wire schema for a target's tally, one non-negative integer per kind. */
export const ReactionCountsSchema = z.object(Object.fromEntries(REACTION_KINDS.map((kind) => [kind, z.number().int().nonnegative()])));
// Reaction data crosses deploys: a tab loaded before a deploy that adds a kind
// receives kinds this bundle has never heard of, and a client ahead of the API
// mid-rollout reads tallies that lack its newest kind. Clients parse every
// response through these at the boundary, so the rest of the app holds only
// known kinds and a complete tally.
/** A kind this build doesn't know reads as no reaction. */
export function parseReactionKind(raw) {
    return REACTION_KINDS.includes(raw)
        ? raw
        : null;
}
/** Keeps only the kinds this build knows; a kind the sender omitted reads 0. */
export function parseReactionCounts(raw) {
    const counts = { ...ZERO_REACTION_COUNTS };
    for (const kind of REACTION_KINDS)
        counts[kind] = raw[kind] ?? 0;
    return counts;
}
/** Parses the tally + viewer's reaction every reactable target carries. */
export function parseReactionState(raw) {
    return {
        ...raw,
        reactionCounts: parseReactionCounts(raw.reactionCounts),
        viewerReaction: parseReactionKind(raw.viewerReaction),
    };
}
/** Every reaction on a target, across kinds. */
export function totalReactions(counts) {
    return REACTION_KINDS.reduce((sum, kind) => sum + counts[kind], 0);
}
// Pure decision for the reaction toggle. Single-reaction-per-target UX:
// tapping the same kind toggles off; tapping a different kind replaces the
// existing reaction(s). The underlying tables permit multi-kind so a future
// "thumbs + star" UX can ship without a migration; the decision boundary is
// where the rule currently lives.
export function decideReactionToggle(existingKinds, requestedKind) {
    // Toggle off: tapping the only reaction the member already holds removes
    // it. If they hold multiple kinds — possible only via direct DB writes —
    // tapping one of them removes that one but leaves the rest; the next
    // 'added' interaction normalizes back to single.
    if (existingKinds.has(requestedKind) && existingKinds.size === 1) {
        return { action: 'removed', kind: requestedKind };
    }
    const replacing = [...existingKinds].filter((k) => k !== requestedKind);
    return { action: 'added', kind: requestedKind, replacing };
}
//# sourceMappingURL=reactions.js.map